import React, { createContext, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import useApi from 'common/useApi';
import httpClient, { setUnauthorizedHandler } from 'common/useApi/httpClient';
import { setSelectedSubaccountId } from 'common/useApi/selectedSubaccount';

export type ISubaccount = {
  id: number
  username: string
  balance: string
  payout_threshold: string
  payout_frequency: string
  last_share_date: string | null
}

type IUser = {
  first_name: string
  last_name: string
  email: string
  payout_threshold: number
  is_superuser: boolean
  has_device: boolean
  is_verified: boolean
  date_verified: string
  timezone: string
  subaccounts: ISubaccount[]
  balance: string
  min_payout_threshold: string
  max_payout_threshold: string
  notify_worker_status: boolean
  notify_payout: boolean
  notify_reward: boolean
  notify_balance_threshold: boolean
  notify_email: boolean
}

type IAuthContext = {
  isLoggingIn: boolean
  login(email: string, password: string): void
  loginError: boolean
  loginErrorDetail: Record<string, string>
  loginSuccessfull: boolean
  isOTPLoggingIn: boolean
  loginOTP(token: string): void
  otpError: boolean
  otpErrorDetail: Record<string, string>
  otpSuccessful: boolean
  otpRequired: boolean
  verificationRequired: boolean
  loginUser(): void
  logout(): void
  refetch(): void
  setRedirect(to: string): void
  setTempToken(to: string): void
  tempToken: string
  user: IUser | null
  authChecked: boolean
  selectedSubaccount: ISubaccount | null
  setSelectedSubaccount(sub: ISubaccount | null): void
}

const AuthContext = createContext<IAuthContext|null>(null);

export const AuthContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<IUser|null>(null);
  const [selectedSubaccount, setSelectedSubaccountState] = useState<ISubaccount | null>(null);
  // S9: false until the initial session probe resolves, so ProtectedRoute
  // doesn't bounce a logged-in user while /api/me is still in flight.
  const [authChecked, setAuthChecked] = useState(false);
  const [redirectTo, setRedirectTo] = useState('/dashboard');
  const [otpRequired, setOtpRequired] = useState(false);
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Mirror of `user` for the once-registered 401 handler, which must read the
  // latest value without re-subscribing. Written during render (safe for a ref).
  const userRef = useRef<IUser | null>(null);

  userRef.current = user;

  const {
    isPending: isLoggingIn,
    mutate,
    isSuccess: loginSuccessfull,
    isError: loginError,
    error: loginErrorDetail,
    data,
  } = useApi('post', '/api/login');

  const {
    isPending: isOTPLoggingIn,
    mutate: mutateOTP,
    isSuccess: otpSuccessful,
    isError: otpError,
    error: otpErrorDetail,
  } = useApi('post', '/api/me/login-otp');

  const {
    isLoading: isFetchingUser,
    isSuccess: userSuccessfull,
    isError: userError,
    data: apiUser,
    refetch: refetchUser,
  } = useApi('get', '/api/me', null, { enabled: false });

  const { mutate: invalidateToken } = useApi('post', '/api/logout');

  const login = (email: string, password: string) => {
    mutate({ email, password });
  };

  // S9: OTP is verified against the session established by /api/login — no
  // bearer token to thread through.
  const loginOTP = (token: string) => {
    mutateOTP({ token });
  };

  // S9: the session cookie is already set by /api/login; just load the user.
  const loginUser = () => {
    refetchUser();
  };

  useEffect(() => {
    if (loginSuccessfull) {
      if (!data.date_verified) {
        setVerificationRequired(true);
      } else if (data.otp_required && !otpSuccessful) {
        setOtpRequired(true);
      } else {
        loginUser();
      }
    }
  }, [loginSuccessfull, data, otpSuccessful]);

  const setSelectedSubaccount = (sub: ISubaccount | null) => {
    setSelectedSubaccountState(sub);
    // Drive the external store too (it owns the localStorage write and notifies
    // the data layer), so the react-query cache re-keys to the new account.
    setSelectedSubaccountId(sub ? String(sub.id) : null);
  };

  useEffect(() => {
    if (userSuccessfull) {
      setUser(apiUser);
      // Restore saved subaccount selection, defaulting to first if only one.
      const saved = localStorage.getItem('selectedSubaccountId');
      const subs: ISubaccount[] = apiUser?.subaccounts ?? [];

      if (subs.length === 1) {
        setSelectedSubaccount(subs[0]);
      } else if (saved) {
        const match = subs.find((s) => String(s.id) === saved);

        setSelectedSubaccount(match ?? null);
      }
      navigate(redirectTo);
    }
  }, [apiUser, userSuccessfull, isFetchingUser]);

  const logout = () => {
    setUser(null);
    setSelectedSubaccount(null);
    invalidateToken();
    queryClient.clear();
    navigate('/login');
  };

  const refetch = () => {
    refetchUser();
  };

  // Handle session expiry centrally: any 401 from the API clears auth and bounces
  // to login. Registered once; ignores 401s before login (userRef is null then),
  // so the initial /api/me/ probe can't trigger a redirect loop.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!userRef.current) {
        return;
      }
      setUser(null);
      setSelectedSubaccount(null);
      queryClient.clear();
      navigate('/login');
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  // S9: on load, set the CSRF cookie and silently probe for an existing
  // session. A failure just means "not logged in" — it must not surface as a
  // login error, so this bypasses the shared query error state.
  useEffect(() => {
    let cancelled = false;

    httpClient.get('/api/csrf/').catch(() => { /* best-effort CSRF bootstrap */ });
    httpClient.get('/api/me/')
      .then(({ data: me }) => {
        if (cancelled) {
          return;
        }
        setUser(me);
        const saved = localStorage.getItem('selectedSubaccountId');
        const subs: ISubaccount[] = me?.subaccounts ?? [];

        if (subs.length === 1) {
          setSelectedSubaccount(subs[0]);
        } else if (saved) {
          const match = subs.find((s: ISubaccount) => String(s.id) === saved);

          setSelectedSubaccount(match ?? null);
        }
      })
      .catch(() => { /* not authenticated */ })
      .finally(() => {
        if (!cancelled) {
          setAuthChecked(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <AuthContext.Provider value={
      {
        isLoggingIn: isLoggingIn || isFetchingUser,
        login,
        loginError: loginError || userError,
        loginErrorDetail: loginErrorDetail?.data,
        loginSuccessfull: loginSuccessfull || userSuccessfull,
        isOTPLoggingIn: isOTPLoggingIn || isFetchingUser,
        loginOTP,
        otpError: otpError || userError,
        otpErrorDetail: otpErrorDetail?.data,
        otpSuccessful: otpSuccessful || userSuccessfull,
        otpRequired,
        verificationRequired,
        loginUser,
        logout,
        refetch,
        tempToken,
        setTempToken,
        setRedirect: setRedirectTo,
        user,
        authChecked,
        selectedSubaccount,
        setSelectedSubaccount,
      }
    }>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
